import { Skeleton } from "@design";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { fetchChatAttachment } from "@/api";
import { Text } from "@/components/Text";

/**
 * An image document, shown from its original file (the server's preview only renders PDF and Office files).
 * *type* comes from the file's extension, so the browser never decodes it as anything but that image type.
 */
export function ImageDocumentPreview({
  attachmentUuid,
  type,
  title,
}: Readonly<{ attachmentUuid: string; type: string; title: string }>) {
  const { t } = useTranslation("chat", { keyPrefix: "documents" });
  const file = useQuery({
    queryKey: ["chat", "document-image", attachmentUuid],
    queryFn: () => fetchChatAttachment(attachmentUuid),
    // A version's file never changes.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  const url = useMemo(
    () => (file.data ? URL.createObjectURL(new Blob([file.data.blob], { type })) : null),
    [file.data, type],
  );
  useEffect(() => () => (url ? URL.revokeObjectURL(url) : undefined), [url]);

  if (file.isPending) return <Skeleton className="aspect-video w-full rounded-md" />;
  if (!url) {
    return (
      <output className="block rounded-lg border border-border-subtle bg-surface-secondary px-4 py-6 text-center">
        <Text tone="secondary">{t("previewError")}</Text>
      </output>
    );
  }
  return (
    <img
      src={url}
      alt={title}
      className="mx-auto max-h-[75dvh] max-w-full rounded-md border border-border-subtle bg-surface-secondary object-contain"
    />
  );
}
